import type { PostProcessingActionCreated } from 'interfaces/postProcessingAction';
import { cloneDeep, filter, map } from 'lodash';

export type PresetInputSection = 'postprocessing' | 'features';
export type PresetActionAccess = 'editable' | 'locked' | 'editable_advanced';

export interface PresetActionEncapsulation {
  section: PresetInputSection;
  actionInstanceId: string;
  access: PresetActionAccess;
}

export interface PresetEncapsulation {
  actions: PresetActionEncapsulation[];
}

interface HighlightedInputReference {
  section: PresetInputSection;
  actionInstanceId: string;
}

interface EncapsulatedPresetJson {
  postprocessing: PostProcessingActionCreated[];
  features: PostProcessingActionCreated[];
  highlightedInputs?: HighlightedInputReference[];
  encapsulation?: PresetEncapsulation;
}

export const getConsumerPresetJson = <PresetJson extends EncapsulatedPresetJson>(
  presetJson: PresetJson,
  canEditAdvancedActions: boolean
): PresetJson => {
  if (!presetJson.encapsulation) {
    return cloneDeep(presetJson);
  }

  const accessByReference = new Map(
    map(
      presetJson.encapsulation.actions,
      (action) => [`${action.section}:${action.actionInstanceId}`, action.access] as const
    )
  );
  const isActionVisible = (section: PresetInputSection, action: PostProcessingActionCreated) => {
    const access = accessByReference.get(`${section}:${action.id}`);
    return access === 'editable' || (access === 'editable_advanced' && canEditAdvancedActions);
  };
  const postprocessing = filter(presetJson.postprocessing, (action) => isActionVisible('postprocessing', action));
  const features = filter(presetJson.features, (action) => isActionVisible('features', action));
  const visibleReferences = new Set([
    ...map(postprocessing, (action) => `postprocessing:${action.id}`),
    ...map(features, (action) => `features:${action.id}`),
  ]);

  return {
    ...cloneDeep(presetJson),
    postprocessing,
    features,
    highlightedInputs: filter(presetJson.highlightedInputs, (input) =>
      visibleReferences.has(`${input.section}:${input.actionInstanceId}`)
    ),
  };
};
